import { useCallback, useMemo, useRef, useState } from 'react';

import { Table } from '@tanstack/react-table';

import { DEFAULT_PAGE_SIZE } from '@/lib/constants/pagination';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { revealTableRow } from '@/modules/finance/common/utils/reveal-table-row';
import { getShortTeamMembersRaw } from '@/modules/team';
import { ShortTeamMemberResponse } from '@/modules/team/types/dto/team-member-short.dto';

import { getBillMemberRowId } from '../../components/generate-bills-table/bill-row.helpers';
import { mapShortTeamMemberToCustomBillRow } from '../../mappers';
import { GenerateBillListItem } from '../../types';

export interface BillCustomMembers {
  customMembers: GenerateBillListItem[];
  addCustomMember: (member: ShortTeamMemberResponse, defaultCurrencyCode: string) => void;
  removeCustomMember: (memberId: string) => void;
  hasCustomMembers: boolean;
  reset: () => void;
  /**
   * Selects, expands and scrolls to rows added since the last render. Call from an effect keyed
   * on the composed table data — a new row is not in TanStack's model until that data rebuilds.
   */
  flushPendingRows: (table: Table<GenerateBillListItem>) => void;
  customMemberIds: Set<number>;
  fetchMembers: (
    search: string,
    cursor: number | null
  ) => Promise<PaginatedResponse<ShortTeamMemberResponse>>;
}

export const useBillCustomMembers = (): BillCustomMembers => {
  const [customMembers, setCustomMembers] = useState<GenerateBillListItem[]>([]);
  const pendingSelections = useRef<string[]>([]);

  const customMemberIds = useMemo(
    () => new Set(customMembers.map(m => m.member.id)),
    [customMembers]
  );

  const addCustomMember = useCallback(
    (member: ShortTeamMemberResponse, defaultCurrencyCode: string) => {
      pendingSelections.current.push(getBillMemberRowId(member.id));
      setCustomMembers(prev => [
        ...prev,
        mapShortTeamMemberToCustomBillRow(member, defaultCurrencyCode),
      ]);
    },
    []
  );

  const removeCustomMember = useCallback((memberId: string) => {
    setCustomMembers(prev => prev.filter(m => m.id !== memberId));
  }, []);

  const flushPendingRows = useCallback((table: Table<GenerateBillListItem>) => {
    const rowIds = pendingSelections.current;
    if (rowIds.length === 0) return;
    pendingSelections.current = [];
    table.setRowSelection(prev => ({
      ...prev,
      ...Object.fromEntries(rowIds.map(id => [id, true])),
    }));
    // The row lands at the bottom of the table, usually below the fold: expand it and bring it
    // into view so the user sees what they just added.
    revealTableRow(table, rowIds[rowIds.length - 1]);
  }, []);

  const reset = useCallback(() => {
    setCustomMembers([]);
  }, []);

  const fetchMembers = useCallback(
    (search: string, cursor: number | null): Promise<PaginatedResponse<ShortTeamMemberResponse>> =>
      getShortTeamMembersRaw({
        filter: search ? { searchString: search } : undefined,
        pagination: { cursor, pageSize: DEFAULT_PAGE_SIZE },
      }),
    []
  );

  return {
    customMembers,
    addCustomMember,
    removeCustomMember,
    hasCustomMembers: customMembers.length > 0,
    reset,
    flushPendingRows,
    customMemberIds,
    fetchMembers,
  };
};
